import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, X, Star } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/cms`;

const CMSTestimonials = () => {
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({
    author: '',
    text: '',
    rating: 5,
    status: 'active'
  });

  useEffect(() => {
    loadTestimonials();
  }, []);

  const loadTestimonials = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/testimonials`);
      setTestimonials(response.data);
    } catch (error) {
      console.error('Error loading testimonials:', error);
      toast.error('Failed to load testimonials');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const data = { ...formData, rating: parseInt(formData.rating) };

      if (editingItem) {
        await axios.put(`${API}/testimonials/${editingItem.id}`, data);
        toast.success('Testimonial updated');
      } else {
        await axios.post(`${API}/testimonials`, data);
        toast.success('Testimonial created');
      }

      resetForm();
      loadTestimonials();
    } catch (error) {
      console.error('Error saving testimonial:', error);
      toast.error('Failed to save testimonial');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({
      author: item.author,
      text: item.text,
      rating: item.rating,
      status: item.status
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this testimonial?')) return;

    try {
      await axios.delete(`${API}/testimonials/${id}`);
      toast.success('Testimonial deleted');
      loadTestimonials();
    } catch (error) {
      console.error('Error deleting testimonial:', error);
      toast.error('Failed to delete testimonial');
    }
  };

  const resetForm = () => {
    setFormData({
      author: '',
      text: '',
      rating: 5,
      status: 'active'
    });
    setEditingItem(null);
    setShowForm(false);
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-3xl font-bold text-[#262626]">Testimonials</h2>
          <p className="text-gray-600">Manage client reviews</p>
        </div>
        <Button
          onClick={() => setShowForm(!showForm)}
          className="bg-[#DAA520] hover:bg-[#B8860B] text-white"
        >
          {showForm ? <X className="mr-2" size={20} /> : <Plus className="mr-2" size={20} />}
          {showForm ? 'Cancel' : 'Add Testimonial'}
        </Button>
      </div>

      {showForm && (
        <Card>
          <CardHeader>
            <CardTitle>{editingItem ? 'Edit Testimonial' : 'New Testimonial'}</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Client Name</label>
                  <Input
                    value={formData.author}
                    onChange={(e) => setFormData({...formData, author: e.target.value})}
                    placeholder="John & Sarah M."
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Rating</label>
                  <select
                    value={formData.rating}
                    onChange={(e) => setFormData({...formData, rating: e.target.value})}
                    className="w-full p-2 border rounded-md"
                  >
                    <option value="5">5 Stars</option>
                    <option value="4">4 Stars</option>
                    <option value="3">3 Stars</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Testimonial Text</label>
                <Textarea
                  value={formData.text}
                  onChange={(e) => setFormData({...formData, text: e.target.value})}
                  rows={4}
                  placeholder="Working with EC Home Builds was an incredible experience..."
                  required
                />
              </div>

              <div className="flex gap-2">
                <Button type="submit" disabled={loading} className="bg-[#DAA520] hover:bg-[#B8860B]">
                  {loading ? 'Saving...' : editingItem ? 'Update' : 'Create'}
                </Button>
                <Button type="button" variant="outline" onClick={resetForm}>
                  Cancel
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      <div className="grid md:grid-cols-2 gap-4">
        {loading && testimonials.length === 0 ? (
          <Card><CardContent className="p-12 text-center">Loading...</CardContent></Card>
        ) : testimonials.length === 0 ? (
          <Card><CardContent className="p-12 text-center text-gray-500">No testimonials yet</CardContent></Card>
        ) : (
          testimonials.map((item) => (
            <Card key={item.id}>
              <CardContent className="p-6">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      {[...Array(item.rating || 5)].map((_, i) => (
                        <Star key={i} size={16} className="fill-[#DAA520] text-[#DAA520]" />
                      ))}
                    </div>
                    <p className="font-bold text-[#262626]">– {item.author}</p>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleEdit(item)}
                    >
                      <Edit size={16} />
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-red-600 hover:bg-red-50"
                      onClick={() => handleDelete(item.id)}
                    >
                      <Trash2 size={16} />
                    </Button>
                  </div>
                </div>
                <p className="text-gray-700 italic">"{item.text}"</p>
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </div>
  );
};

export default CMSTestimonials;